import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Table, Select, Button, Space, Spin, Empty, Tooltip, Tag, Typography } from 'antd';
import { ReloadOutlined, FileExcelOutlined, BarChartOutlined } from '@ant-design/icons';
import Layout from '../../components/Layout';
import axiosInstance from '../../variables/api/axiosInstance';
import { toast } from 'react-toastify';
import { Trans, useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { localeIntl } from '../../i18n/format';
import { libellesTraduits } from '../../i18n/libelles';
import { XLSX } from '../../utils/xlsxShim';
import { getUser } from '../../variables/constants';
import PageHeader from '../../components/PageHeader';

const { Text } = Typography;

const fCFA = (v) => v == null ? '-' : Number(v).toLocaleString(localeIntl()) + ' FCFA';
const fDate = (v) => {
    if (!v) return '-';
    try { return new Date(v).toLocaleDateString(localeIntl()); } catch { return v; }
};

const STATUT_LABEL = libellesTraduits('statistiques:reclamationsTogo.statuts');

const STATUT_COLOR = {
    BON_A_PAYER: 'green',
    ATTENTE_OFFRE: 'orange',
    PROBLEME_RC: 'red',
    ARBITRAGE: 'purple',
    ATTENTE_PIECES: 'blue',
    CLOTURE: 'default',
    AUTRES: 'default',
};

// ── Colonnes du tableau de détail ─────────────────────────────────────────────
const DETAIL_COLS = (t) => [
    { title: t('reclamationsTogo.colonnes.numero'), dataIndex: 'numero', key: 'num', width: 45, align: 'center', fixed: 'left' },
    {
        title: t('reclamationsTogo.colonnes.dateSinistre'), dataIndex: 'dateAccident', key: 'date', width: 120,
        render: fDate, export: (v) => fDate(v),
    },
    { title: t('reclamationsTogo.colonnes.sinBncbTg'), dataIndex: 'sinBncbTg', key: 'sinTg', width: 150 },
    { title: t('reclamationsTogo.colonnes.sinBncbPartenaire'), dataIndex: 'sinBncbPartenaire', key: 'sinPart', width: 150 },
    { title: t('reclamationsTogo.colonnes.sinAssureur'), dataIndex: 'sinAssureurPartenaire', key: 'sinAss', width: 140 },
    { title: t('reclamationsTogo.colonnes.assureCie'), dataIndex: 'assure', key: 'assure', width: 170, ellipsis: true },
    { title: t('reclamationsTogo.colonnes.victimes'), dataIndex: 'victime', key: 'victime', width: 160, ellipsis: true },
    {
        title: t('reclamationsTogo.colonnes.montantReclame'), dataIndex: 'montantReclame', key: 'mtrec', align: 'right', width: 150,
        render: fCFA, export: (v) => (v == null ? '' : Number(v)), numeric: true,
    },
    {
        title: t('reclamationsTogo.colonnes.montantRetenu'), dataIndex: 'montantRetenu', key: 'mtret', align: 'right', width: 150,
        render: fCFA, export: (v) => (v == null ? '' : Number(v)), numeric: true,
    },
    {
        title: t('reclamationsTogo.colonnes.situation'), dataIndex: 'statutReclamation', key: 'statut', width: 140,
        render: (v) => <Tag color={STATUT_COLOR[v] || 'default'}>{STATUT_LABEL[v] || v || '-'}</Tag>,
        export: (v) => STATUT_LABEL[v] || v || '',
    },
    { title: t('reclamationsTogo.colonnes.observations'), dataIndex: 'observations', key: 'obs', ellipsis: true, width: 200 },
    { title: t('reclamationsTogo.colonnes.assureurTg'), dataIndex: 'assureurTg', key: 'asstg', width: 160, ellipsis: true },
];

// ── Colonnes du tableau récap ─────────────────────────────────────────────────
const buildRecapCols = (t) => [
    {
        title: t('reclamationsTogo.colonnes.pays'), dataIndex: 'pays', key: 'pays', width: 150,
        render: (v, r) => <span style={{ fontWeight: r._isTotal ? 700 : 400 }}>{v}</span>,
    },
    { title: t('reclamationsTogo.colonnes.compagnie'), dataIndex: 'compagnie', key: 'comp', width: 200 },
    {
        title: t('reclamationsTogo.colonnes.nbDossiers'), dataIndex: 'nbDossiers', key: 'nb', align: 'right', width: 110,
        render: (v, r) => <span style={{ fontWeight: r._isTotal ? 700 : 400 }}>{v}</span>,
    },
    {
        title: t('reclamationsTogo.colonnes.montantReclame'), dataIndex: 'montantReclame', key: 'mt', align: 'right', width: 160,
        render: (v, r) => <span style={{ fontWeight: r._isTotal ? 700 : 400 }}>{fCFA(v)}</span>,
    },
    {
        title: t('reclamationsTogo.colonnes.montantRetenu'), dataIndex: 'montantRetenu', key: 'ret', align: 'right', width: 150,
        render: (v, r) => <span style={{ fontWeight: r._isTotal ? 700 : 400 }}>{fCFA(v)}</span>,
    },
    {
        title: t('reclamationsTogo.colonnes.pctNb'), dataIndex: 'pctNb', key: 'pctnb', align: 'right', width: 80,
        render: (v, r) => r._isTotal ? <strong>100 %</strong> : (v == null ? '-' : `${v.toFixed(1)} %`),
    },
    {
        title: t('reclamationsTogo.colonnes.pctMontant'), dataIndex: 'pctMontant', key: 'pctmt', align: 'right', width: 100,
        render: (v, r) => r._isTotal ? <strong>100 %</strong> : (v == null ? '-' : `${v.toFixed(1)} %`),
    },
];

// ── Export Excel multi-feuilles ───────────────────────────────────────────────
async function exportR6(data, codePays) {
    if (!data?.blocs?.length) { toast.warn(i18n.t('statistiques:reclamationsTogo.aucuneDonneeAExporter')); return; }

    const blocs = codePays ? data.blocs.filter(b => b.codePays === codePays) : data.blocs;
    if (!blocs.length) { toast.warn(i18n.t('statistiques:reclamationsTogo.aucuneDonneePays')); return; }

    const user = getUser();
    const userLabel = user ? (`${user.prenom || ''} ${user.nom || ''}`).trim() || user.login || '-' : '-';
    const dateLabel = new Date().toLocaleDateString(localeIntl());
    const wb = XLSX.utils.book_new();

    const detailHeaders = [
        'numero', 'dateSinistre', 'sinBncbTg', 'sinBncbPartenaire', 'sinAssureur',
        'assureCie', 'victimes', 'montantReclame', 'montantRetenu',
        'situation', 'observations', 'assureurTg',
    ].map((cle) => i18n.t(`statistiques:reclamationsTogo.excel.entetes.${cle}`));

    // ── Une feuille par compagnie ──
    for (const bloc of blocs) {
        for (const comp of bloc.compagnies) {
            const sheetName = comp.compagnie.replace(/[:\\/?*[\]]/g, '-').slice(0, 31);

            const meta = [
                [i18n.t('statistiques:reclamationsTogo.excel.bureau')],
                [i18n.t('statistiques:reclamationsTogo.excel.titreFeuille', { pays: bloc.pays, code: bloc.codePays })],
                [i18n.t('statistiques:reclamationsTogo.excel.compagnie', { compagnie: comp.compagnie })],
                [i18n.t('statistiques:reclamationsTogo.excel.exporteLe', { date: dateLabel, utilisateur: userLabel })],
                [],
            ];

            const dataRows = comp.dossiers.map(d => [
                d.numero,
                d.dateAccident ? new Date(d.dateAccident).toLocaleDateString(localeIntl()) : '',
                d.sinBncbTg || '',
                d.sinBncbPartenaire || '',
                d.sinAssureurPartenaire || '',
                d.assure || '',
                d.victime || '',
                Number(d.montantReclame) || 0,
                Number(d.montantRetenu) || 0,
                STATUT_LABEL[d.statutReclamation] || d.statutReclamation || '',
                d.observations || '',
                d.assureurTg || '',
            ]);

            const totalRec = comp.dossiers.reduce((s, d) => s + (Number(d.montantReclame) || 0), 0);
            const totalRet = comp.dossiers.reduce((s, d) => s + (Number(d.montantRetenu) || 0), 0);
            const totalRow = ['TOTAL', '', '', '', '', '', '', totalRec, totalRet, '', '', ''];

            const ws = XLSX.utils.aoa_to_sheet([...meta, detailHeaders, ...dataRows, totalRow]);

            ws['!cols'] = [
                { wch: 5 }, { wch: 14 }, { wch: 18 }, { wch: 18 }, { wch: 16 },
                { wch: 22 }, { wch: 22 }, { wch: 20 }, { wch: 20 },
                { wch: 18 }, { wch: 28 }, { wch: 20 },
            ];

            // Format monétaire pour colonnes H et I
            const dataStart = meta.length + 2; // 1 pour headers
            for (let rowIdx = dataStart; rowIdx <= dataStart + dataRows.length; rowIdx++) {
                ['H', 'I'].forEach(col => {
                    const ref = `${col}${rowIdx}`;
                    if (ws[ref]) ws[ref].z = '#,##0';
                });
            }

            XLSX.utils.book_append_sheet(wb, ws, sheetName);
        }
    }

    // ── Feuille RECAP ──
    const recapRows = codePays
        ? data.recap.filter(r => r.codePays === codePays)
        : data.recap;

    const totalNb = recapRows.reduce((s, r) => s + r.nbDossiers, 0);
    const totalMt = recapRows.reduce((s, r) => s + (Number(r.montantReclame) || 0), 0);
    const totalRet = recapRows.reduce((s, r) => s + (Number(r.montantRetenu) || 0), 0);

    const recapMeta = [
        [i18n.t('statistiques:reclamationsTogo.excel.bureau')],
        [i18n.t('statistiques:reclamationsTogo.excel.titreRecap')],
        [i18n.t('statistiques:reclamationsTogo.excel.exporteLe', { date: dateLabel, utilisateur: userLabel })],
        [],
    ];
    const recapHeaders = ['pays', 'compagnie', 'nbDossiers', 'montantReclame', 'montantRetenu', 'pctNb', 'pctMontant']
        .map((cle) => i18n.t(`statistiques:reclamationsTogo.excel.entetesRecap.${cle}`));
    const recapData = recapRows.map(r => [
        r.pays,
        r.compagnie,
        r.nbDossiers,
        Number(r.montantReclame) || 0,
        Number(r.montantRetenu) || 0,
        `${(r.pctNb || 0).toFixed(1)} %`,
        `${(r.pctMontant || 0).toFixed(1)} %`,
    ]);
    const recapTotal = ['TOTAL', '', totalNb, totalMt, totalRet, '100 %', '100 %'];

    const wsRecap = XLSX.utils.aoa_to_sheet([...recapMeta, recapHeaders, ...recapData, recapTotal]);
    wsRecap['!cols'] = [
        { wch: 20 }, { wch: 25 }, { wch: 13 }, { wch: 22 }, { wch: 22 }, { wch: 10 }, { wch: 12 },
    ];

    // Format monétaire recap (colonnes D et E)
    const recapDataStart = recapMeta.length + 2;
    for (let rowIdx = recapDataStart; rowIdx <= recapDataStart + recapData.length; rowIdx++) {
        ['D', 'E'].forEach(col => {
            const ref = `${col}${rowIdx}`;
            if (wsRecap[ref]) wsRecap[ref].z = '#,##0';
        });
    }

    XLSX.utils.book_append_sheet(wb, wsRecap, 'RECAP');

    const slug = new Date().toISOString().slice(0, 10);
    const suffix = codePays ? `-${codePays}` : '';
    await XLSX.writeFile(wb, `R6-reclamations-togo${suffix}_${slug}.xlsx`);
}

// ════════════════════════════════════════════════════════════════════════════
//  COMPOSANT
// ════════════════════════════════════════════════════════════════════════════
export default function ReclamationsTogo() {
    const { t } = useTranslation('statistiques');
    const [codePays, setCodePays] = useState('');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/v1/stats/reclamations-togo');
            setData(res.data?.data);
        } catch {
            toast.error(t('reclamationsTogo.erreurChargement'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => { load(); }, [load]);

    // Pays disponibles extraits des données chargées
    const paysOptions = useMemo(() => {
        if (!data?.blocs) return [];
        return data.blocs.map(b => ({ value: b.codePays, label: `${b.pays} (${b.codePays})` }));
    }, [data]);

    // Blocs visibles selon le filtre pays
    const visibleBlocs = useMemo(() => {
        if (!data?.blocs) return [];
        return codePays ? data.blocs.filter(b => b.codePays === codePays) : data.blocs;
    }, [data, codePays]);

    // Compte : les dossiers reellement listes dans les tableaux visibles ; le
    // recapitulatif, lui, agrege par compagnie et porte une ligne TOTAL.
    const nbDossiersAffiches = useMemo(
        () => visibleBlocs.reduce(
            (n, bloc) => n + (bloc.compagnies || []).reduce(
                (m, comp) => m + (comp.dossiers?.length || 0), 0),
            0),
        [visibleBlocs],
    );

    // Lignes recap filtrées + ligne total
    const recapRows = useMemo(() => {
        if (!data?.recap) return [];
        const rows = codePays
            ? data.recap.filter(r => r.codePays === codePays)
            : data.recap;
        const totalNb = rows.reduce((s, r) => s + r.nbDossiers, 0);
        const totalMt = rows.reduce((s, r) => s + (Number(r.montantReclame) || 0), 0);
        const totalRet = rows.reduce((s, r) => s + (Number(r.montantRetenu) || 0), 0);
        return [
            ...rows.map((r, i) => ({ ...r, key: i })),
            {
                key: 'total', pays: 'TOTAL', compagnie: '',
                nbDossiers: totalNb, montantReclame: totalMt, montantRetenu: totalRet,
                pctNb: null, pctMontant: null, _isTotal: true,
            },
        ];
    }, [data, codePays]);

    const recapCols = useMemo(() => buildRecapCols(t), [t]);

    return (
        <Layout title={t('reclamationsTogo.titreOnglet')}>
            <div style={{ maxWidth: 1280 }}>

                {/* En-tête */}
                <PageHeader
                    titre={t('reclamationsTogo.titre')}
                    icone={<BarChartOutlined />}
                    compte={data ? nbDossiersAffiches : undefined}
                    unite={t('reclamationsTogo.unite')}
                    actions={(
                        <Space>
                            <Select
                                value={codePays || undefined}
                                onChange={(v) => setCodePays(v || '')}
                                placeholder={t('reclamationsTogo.tousPays')}
                                allowClear
                                style={{ width: 210, borderRadius: 8 }}
                                options={paysOptions}
                                loading={loading}
                            />
                            <Tooltip title={t('commun.actualiser')}>
                                <Button icon={<ReloadOutlined />} onClick={load} loading={loading} style={{ borderRadius: 8 }} />
                            </Tooltip>
                            <Tooltip title={t('reclamationsTogo.aideExport')}>
                                <Button
                                    icon={<FileExcelOutlined />}
                                    onClick={() => exportR6(data, codePays)}
                                    disabled={!data || !visibleBlocs.length}
                                    style={{ borderRadius: 8, color: '#166534', borderColor: '#bbf7d0' }}
                                >
                                    {t('reclamationsTogo.exportExcel')}
                                </Button>
                            </Tooltip>
                        </Space>
                    )}
                />

                {/* ── Blocs par pays / compagnie ─────────────────────────── */}
                <Spin spinning={loading}>
                    {!loading && visibleBlocs.length === 0 ? (
                        <Empty description={t('commun.aucuneDonnee')} style={{ padding: 60 }} />
                    ) : (
                        visibleBlocs.map((bloc) => (
                            <div key={bloc.codePays} style={{ marginBottom: 32 }}>
                                {/* Bandeau pays */}
                                <div style={{
                                    background: '#1a6b3c', color: '#fff',
                                    padding: '10px 18px', borderRadius: '8px 8px 0 0',
                                    fontWeight: 700, fontSize: 14,
                                    display: 'flex', alignItems: 'center', gap: 8,
                                }}>
                                    <BarChartOutlined />
                                    <span>{t('reclamationsTogo.bandeauPays', { pays: bloc.pays, code: bloc.codePays })}</span>
                                </div>

                                {/* Tableaux par compagnie */}
                                {bloc.compagnies.map((comp) => (
                                    <div key={comp.compagnie} className="cb-card" style={{ padding: 0, marginBottom: 12, borderRadius: 0 }}>
                                        {/* Sous-header compagnie */}
                                        <div style={{
                                            padding: '9px 16px',
                                            background: '#f0fdf4', borderBottom: '1px solid #d1fae5',
                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                            flexWrap: 'wrap', gap: 8,
                                        }}>
                                            <span style={{ fontWeight: 700, color: '#1a6b3c', fontSize: 13 }}>
                                                {comp.compagnie}
                                            </span>
                                            <Space size={20}>
                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                    {t('reclamationsTogo.nbDossiers', { count: comp.nbDossiers })}
                                                </Text>
                                                <Text style={{ fontSize: 12, color: '#374151' }}>
                                                    <Trans t={t} i18nKey="reclamationsTogo.reclame" values={{ montant: fCFA(comp.montantReclame) }}
                                                        components={{ strong: <strong style={{ color: '#1e40af' }} /> }} />
                                                </Text>
                                                <Text style={{ fontSize: 12, color: '#374151' }}>
                                                    <Trans t={t} i18nKey="reclamationsTogo.retenu" values={{ montant: fCFA(comp.montantRetenu) }}
                                                        components={{ strong: <strong style={{ color: '#166534' }} /> }} />
                                                </Text>
                                            </Space>
                                        </div>
                                        <Table
                                            className="cb-table"
                                            dataSource={comp.dossiers.map((d, i) => ({ ...d, key: i }))}
                                            columns={DETAIL_COLS(t)}
                                            pagination={false}
                                            size="small"
                                            scroll={{ x: 1400 }}
                                        />
                                    </div>
                                ))}
                            </div>
                        ))
                    )}
                </Spin>

                {/* ── Récapitulatif ─────────────────────────────────────── */}
                {recapRows.length > 1 && (
                    <div className="cb-card" style={{ padding: 0, marginTop: 8 }}>
                        <div style={{
                            padding: '12px 18px 8px', borderBottom: '1px solid #f3f4f6',
                            display: 'flex', alignItems: 'center', gap: 8,
                        }}>
                            <BarChartOutlined style={{ color: '#1a6b3c', fontSize: 16 }} />
                            <span style={{ fontWeight: 700, color: '#1a6b3c', fontSize: 14 }}>
                                {t('reclamationsTogo.recapitulatif')}
                            </span>
                        </div>
                        <Table
                            className="cb-table"
                            dataSource={recapRows}
                            columns={recapCols}
                            pagination={false}
                            size="small"
                            scroll={{ x: 860 }}
                            rowClassName={(r) => r._isTotal ? 'ant-table-row-total' : ''}
                        />
                    </div>
                )}

                {/* Source */}
                {data && (
                    <div style={{ marginTop: 12, fontSize: 11, color: '#9ca3af', textAlign: 'right' }}>
                        {t('commun.sourceGenereLe', { date: new Date().toLocaleDateString(localeIntl()) })}
                    </div>
                )}
            </div>

            <style>{`
                .ant-table-row-total td { background: #f0faf4 !important; font-weight: 700 !important; }
                .ant-table-row-total:hover td { background: #dcfce7 !important; }
            `}</style>
        </Layout>
    );
}
